/**
 * İçerik kartı.
 *
 * Bu bileşen üç turda elden geçirildi; değişikliklerin gerekçesi aşağıda.
 *
 * ## 1. Kapak görseli durumları (B-1)
 *
 * Görsel yüklenemezse `<img>` gizleniyor ve geriye **boş bir degrade kutu**
 * kalıyordu. Kullanıcı içeriğin var olup olmadığını anlayamıyordu; gerçek
 * ekran görüntüsünde "Aydilge" kartı tam olarak boştu.
 *
 * Artık üç durum var ve üçü de tasarlanmış:
 *   - `loading` → poster oranını koruyan iskelet (yükleme sırasında düzen zıplamaz)
 *   - `ready`   → görsel
 *   - `error`   → **monogram rozeti** (harf + renk), karar: "monogram orta yol"
 *
 * ## 2. Eylem düğmesi kaldırıldı (B-2)
 *
 * Kart zaten tamamen tıklanabilir (`<a>` + `onClick`), ve her çağrı yerinde
 * düğmenin yaptığı iş **birebir aynıydı**:
 *
 * ```tsx
 * <ItemCard href="#/izle/{id}" onClick={() => onWatch(id)}
 *           action={<button onClick={() => onWatch(id)}>▶ Oynat</button>} />
 * ```
 *
 * Yani ekranda aynı eylemin **iki** kopyası vardı. Ayrıca düğme
 * `button button--primary card__action` sınıflarını birlikte taşıyordu;
 * `.card__action` (styles.css:1269) ile `.button--primary` (styles.css:1563)
 * **aynı özgüllükte** olduğu için dosyada sonra gelen kazanıyor, kartın
 * tasarlanan ince camgöbeği dolgusu sessizce eziliyor ve her kartta tam
 * genişlik **dolu mavi** bir dikdörtgen görünüyordu.
 *
 * Düzeltme düğmeyi kaldırmak: fazlalığı yok eder, CSS sıralamasına bağımlı
 * kırılma ortadan kalkar ve kapak görselleri yerine bırakılan alan geri gelir.
 * Oynatma eyleminin görsel karşılığı zaten `.card__poster-overlay` katmanı.
 *
 * Gerçekten **farklı** bir eylem gerekiyorsa `action` hâlâ kullanılabilir
 * (ikincil görünümle).
 *
 * ## 3. Gövde hizası (B-6)
 *
 * `durationMs` yoksa süre span'i hiç basılmıyor ve gövde bir satır kısalıyor;
 * yan yana kartların alt kenarları hizasız kalıyordu. Artık süre olmasa da
 * yer tutar (`—`), meta satırı sabit yüksekliktedir.
 */
import type { CSSProperties, ReactNode } from 'react';
import { useState } from 'react';
import type { Item } from '../api.ts';
import { displayTitle, formatDuration, progressRatio } from '../format.ts';
import { FilmIcon, MusicIcon, PlayIcon, TrashIcon, TvIcon } from '../Icon.tsx';
import { proxyImageSrcSet, resizeProxyImage } from '../imageSize.ts';
import { mediaCopy } from '../mediaCopy.ts';
import { isAudioItem } from '../mediaKind.ts';
import { mediaUrl } from '../mediaToken.ts';
import { monogramGradient, monogramOf } from '../monogram.ts';
import { useItemDelete } from './ItemDelete.tsx';

const KIND_LABEL: Record<string, string> = {
  movie: 'Film',
  show: 'Dizi',
  season: 'Sezon',
  episode: 'Bölüm',
  music: 'Müzik',
};

/** Kapak görselinin yüklenme durumu. */
type ThumbnailState = 'loading' | 'ready' | 'error';

/**
 * İçerik ses mi? Tek belirleme `mediaKind.ts` modülünde yaşar
 * (`HeroBanner` de aynı soruyu soruyordu; iki kopya ayrışmasın).
 */

/**
 * Render anındaki kapak durumu.
 *
 * - **Sesli içerik**: kapak görseli yok; `<img>` hiç basılmaz, `load`/`error`
 *   olayı hiç tetiklenmez. `loading`'de kalırsa iskelet sonsuza kadar döner;
 *   bu yüzden doğrudan "görsel yok" (`error`) durumundan başlar ve monogram
 *   görünür.
 * - **Kimlik değişmiş**: durum `loading` okunur, önceki görsel bir kare bile
 *   yanlış içerik için gösterilmez.
 */
function resolveThumb(
  thumbFor: { id: string; state: ThumbnailState },
  itemId: string,
  isAudio: boolean,
): ThumbnailState {
  if (isAudio) return 'error';
  return thumbFor.id === itemId ? thumbFor.state : 'loading';
}

/**
 * Kapak yedeğindeki tür silueti (D-10).
 *
 * Ayrı bileşen: `ItemCard`in koşullu işaretlemesi zaten karmaşıklık
 * sınırına yakındı; iç içe üçlü koşul yerine tek sorumluluklu bir yardımcı.
 */
function MonogramMark({ kind, isAudio }: { kind: string; isAudio: boolean }): ReactNode {
  if (isAudio) return <MusicIcon size={72} />;
  if (kind === 'movie') return <FilmIcon size={72} />;
  return <TvIcon size={72} />;
}

/** Tür etiketi: sesli içerik her zaman "Müzik". */
function kindLabelOf(kind: string, isAudio: boolean): string {
  return isAudio ? 'Müzik' : (KIND_LABEL[kind] ?? kind);
}

/** Bilinmeyen süre sınıfı (B-6 yer tutucusu). */
function durationClassOf(durationMs: number | null): string {
  return durationMs === null || durationMs <= 0
    ? 'card__duration card__duration--unknown'
    : 'card__duration';
}

/** Kalan süre; süre/konum bilinmiyorsa `null`. */
function remainingMsOf(item: Item, resumeMs: number | undefined): number | null {
  if (resumeMs === undefined || item.durationMs === null) return null;
  return Math.max(0, item.durationMs - resumeMs);
}

/** İlerleme rozeti metni: kalan süre varsa onu, yoksa yüzdeyi gösterir. */
function progressBadgeText(remainingMs: number | null, ratio: number): string {
  if (remainingMs !== null && remainingMs > 0) {
    return `${formatDuration(remainingMs)} kaldı`;
  }
  return `%${Math.round(ratio * 100)}`;
}

/**
 * Kullanılacak metadata afişi; `null` = yerel thumbnail.
 *
 * `posterFor` kimliği öğeyle eşleşmiyorsa eski öğenin hatası yeni karta
 * taşınmaz (kart listelerinde `key` unutulsa bile doğru davranış).
 */
function metadataPosterFor(
  item: Item,
  posterFor: { id: string; failedUrl: string | null },
): string | null {
  if (item.posterUrl === null || item.posterUrl === undefined) return null;
  // Aynı öğe için **aynı** URL başarısız olduysa yerel thumbnail'a düş.
  // Metadata yenilenip URL değişirse yeni adres yeniden denenir.
  if (posterFor.id === item.id && posterFor.failedUrl === item.posterUrl) return null;
  return item.posterUrl;
}

export function ItemCard({
  item,
  href,
  action,
  layout = 'portrait',
  onClick,
  /** Soğuk katman erişilemiyorsa kart üzerinde durum rozeti gösterilir. */
  unreachable = false,
  /**
   * Kütüphanenin medya türü. `music` olduğunda "FİLM" yerine "MÜZİK"
   * yazılır.
   *
   * Neden gerekiyor: `isAudio` yalnızca **dosya uzantısına** bakıyor ve
   * katalog başlıkları uzantısız ("Aydilge - 05 - İstanbul"). Böylece müzik
   * kütüphanesindeki parçalar ekranda "FİLM" rozetiyle görünüyordu —
   * ekran görüntüsünde doğrulandı.
   */
  mediaKind = 'video',
}: {
  item: Item;
  href: string;
  action?: ReactNode;
  layout?: 'portrait' | 'landscape' | 'square';
  onClick?: (() => void) | undefined;
  unreachable?: boolean;
  mediaKind?: 'video' | 'music';
}): ReactNode {
  const resumeMs = item.resumePositionMs ?? item.progress?.positionMs;
  const ratio = progressRatio(resumeMs, item.durationMs);
  /*
   * "Kaldı" etiketi **kalan** süreyi göstermeli; önceden izlenen konum
   * (`resumeMs`) yazılıyordu. 60 dk'lık içerikte 10. dakika "50:00 kaldı"
   * olmalı, "10:00 kaldı" değil.
   */
  const remainingMs = remainingMsOf(item, resumeMs);
  const { canDelete, requestDelete } = useItemDelete();

  const isAudio = isAudioItem(item, mediaKind);
  const copy = mediaCopy(isAudio ? 'music' : 'video');

  const kindLabel = kindLabelOf(item.kind, isAudio);

  /*
   * Görsel durumu, **hangi öğeye ait olduğuyla birlikte** tutulur.
   *
   * Kartlar `key={item.id}` ile listeleniyor, yani React normalde yeni
   * bileşen mounts ediyor. Ama buna güvenmek kırılgan: bir çağrı yeri `key`
   * unutursa durum eski görselden taşınır.
   *
   * `useEffect` ile sıfırlamak yerine **render sırasında türetiyoruz**:
   * kimlik değiştiği anda durum `loading` okunur, önceki görsel bir kare bile
   * yanlış içerik için gösterilmez. `useEffect` bunu bir boyna geç yapardı
   * (eski görsel bir kare görünürdü) ve ayrıca "gereksiz bağımlılık" uyarısı
   * doğuruyordu.
   */
  const [thumbFor, setThumbFor] = useState<{ id: string; state: ThumbnailState }>({
    id: item.id,
    state: 'loading',
  });
  const thumb = resolveThumb(thumbFor, item.id, isAudio);
  const setThumb = (state: ThumbnailState): void => setThumbFor({ id: item.id, state });

  /*
   * Metadata afişi başarısız olursa **bir kez** yerel thumbnail denenir.
   *
   * DOM'daki `img.src`i karşılaştırmak yerine niyeti durumda tutuyoruz:
   * tarayıcılar göreli yolu mutlak URL'ye çevirdiği için `endsWith` kırılgan;
   * ayrıca aynı öğe için ikinci hatada monograma geçiş deterministik olur.
   */
  const [posterFor, setPosterFor] = useState<{ id: string; failedUrl: string | null }>({
    id: item.id,
    failedUrl: null,
  });
  const thumbnailSrc = metadataPosterFor(item, posterFor);
  const thumbnailRenderSrc =
    resizeProxyImage(thumbnailSrc, 'w342') ??
    item.thumbnailUrl ??
    mediaUrl(`/api/items/${item.id}/thumbnail`);
  const thumbnailSrcSet = proxyImageSrcSet(thumbnailSrc, 'w342', 'w500');

  const title = displayTitle(item);

  /*
   * Bilinmeyen süre işareti (B-6 + liste görünümü).
   *
   * Izgarada `—` **yer tutucudur** ve kaldırılamaz: meta satırı kısalırsa
   * yan yana kartların alt kenarları hizasız kalır. Satırda ise her satırda
   * tekrarlanan "—" sütunu tek bir bilgi taşımaz; `.grid.view-list` bu sınıfı
   * gizler. Karar CSS'te, işaretleme yalnızca sınıfı taşır.
   */
  const durationClass = durationClassOf(item.durationMs);
  /*
   * Monogram **ham** başlıktan üretilir, `displayTitle`'dan değil.
   *
   * `displayTitle` yıl ve sezon/bölüm numarasını ekler: "Silo (2023)",
   * "S01E01 · Çiçek Taksi". Bunlardan monogram üretilirse kartlarda "S2" ya da
   * "S0" gibi **anlamsız** kısaltmalar çıkar. Ham başlık her zaman anlamlı
   * iki harf verir (Silo → SI, Çiçek Taksi → ÇT).
   */
  const monogram = monogramOf(item.title);
  const showProgress = ratio > 0 && ratio < 0.95;
  /*
   * İlerleme göstergesi **kart başına bir tane** (D-11).
   *
   * Önceden dairesel rozet ve alt çubuk aynı `showProgress` koşuluyla birlikte
   * basılıyordu: aynı bilgi iki kez, kartın görsel alanı ikiye bölünmüş.
   * Yerleşime göre tek gösterge seçilir:
   *   - dikey (2:3) / kare (1:1) → halka (poster köşesinde okunur)
   *   - yatay (16:9) raf        → alt çubuk (halka tür rozetiyle çakışır)
   *   - liste                    → halka zaten CSS'te gizli
   */
  const showRing = showProgress && layout !== 'landscape';
  const showBar = showProgress && layout === 'landscape';
  const layoutClass = layout !== 'portrait' ? `card--${layout}` : '';
  const posterLayoutClass = layout !== 'portrait' ? `card__poster--${layout}` : '';

  return (
    <article className={`card ${layoutClass}`}>
      <a
        className="card__link"
        href={href}
        data-item-id={item.id}
        onClick={(e) => {
          if (onClick) {
            e.preventDefault();
            onClick();
          }
        }}
      >
        <div
          className={`card__poster card__poster--${item.kind} ${posterLayoutClass}`}
          aria-hidden="true"
        >
          {/* 1) Yükleniyor: poster oranını koruyan iskelet */}
          {thumb === 'loading' && <div className="card__skeleton" />}

          {/*
            2) Monogram yedeği.
            Görsel `error` durumuna geçtiğinde görünür olur. `loading`
            durumunda gizlidir: yükleme sırasında kısa bir an monogram
            yanıp sönmesin.
          */}
          {thumb === 'error' && (
            <div className="card__monogram" style={{ background: monogramGradient(item.id) }}>
              {/*
                Tür silueti (B-28 / D-10).
                Monogram tek başına düz renkli bir kutu gibi okunuyordu; özellikle
                müzik kütüphanesinde 10 kartın tamamı birbirinden yalnız harf ve
                tonla ayrışıyordu. Düşük opaklıkta tür ikonu, kapağın "ne olduğunu"
                tek bakışta söyler; metin okunabilirliğini bozmaz.
              */}
              <span className="card__monogram-mark" aria-hidden="true">
                <MonogramMark kind={item.kind} isAudio={isAudio} />
              </span>
              <span className="card__monogram-text">{monogram}</span>
            </div>
          )}

          {/*
            Sesli içerikte `<img>` hiç basılmaz; monogram doğrudan bu
            durumdan gelir (`thumb === 'error'` yukarıda sabitleniyor).
          */}
          {!isAudio && (
            <img
              src={thumbnailRenderSrc}
              srcSet={thumbnailSrcSet}
              alt=""
              loading="lazy"
              decoding="async"
              className="card__thumbnail"
              data-state={thumb}
              onLoad={() => setThumb('ready')}
              onError={() => {
                // Metadata afişi yüklenemezse önce yerel thumbnail denenir;
                // o da yoksa monogram yedeği gösterilir.
                if (thumbnailSrc !== null) {
                  setPosterFor({ id: item.id, failedUrl: thumbnailSrc });
                  return;
                }
                setThumb('error');
              }}
            />
          )}

          {/*
            Dairesel ilerleme rozeti (Tasarım V2, B-27/Dalga 10).

            Yüzde, kartın altındaki ince çubuktan çok daha hızlı okunur:
            kullanıcı ızgara kaydırırken "nerede kaldım" sorusunu rozetden
            görür. `conic-gradient` ile çizilir; ayrı SVG veya kütüphane
            gerekmez.
          */}
          {showRing && (
            <span
              className="card__progress-ring"
              style={{ '--card-progress': `${Math.round(ratio * 360)}deg` } as CSSProperties}
              aria-hidden="true"
            >
              <span className="card__progress-ring-text">{Math.round(ratio * 100)}%</span>
            </span>
          )}

          {/* 3) Oynatma eyleminin görsel karşılığı.
              Düğme değil `div`: kart zaten bir `<a>` ve HTML, bağlantının
              içine etkileşimli öğe koymayı yasaklar. `aria-hidden` yeterli
              çünkü aynı eylem klavyeyle zaten karta odaklanınca çalışır. */}
          <div className="card__poster-overlay">
            <span className="card__play-icon">
              <PlayIcon size={22} />
            </span>
          </div>

          <span className={`card__kind ${isAudio ? 'card__kind--music' : ''}`}>{kindLabel}</span>

          {unreachable && <span className="card__kind card__kind--down">Erişilemiyor</span>}

          {/* İlerleme Çubuğu: Kart görselinin tam alt kenarına entegre */}
          {showBar && (
            <div className="card__progress-bar" aria-hidden="true">
              <div
                className="card__progress-fill"
                style={{ width: `${Math.round(ratio * 100)}%` }}
              />
            </div>
          )}
        </div>

        <div className="card__body">
          <h3 className="card__title" title={title}>
            {title}
          </h3>

          <div className="card__meta">
            <span className={durationClass}>{formatDuration(item.durationMs)}</span>
            {item.isWatched && <span className="badge badge--watched">{copy.watched}</span>}
            {item.progress && !item.isWatched && showProgress && (
              <span className="badge badge--progress">{progressBadgeText(remainingMs, ratio)}</span>
            )}
          </div>
        </div>
      </a>

      {/*
        Yönetici için kalıcı silme. Kartın `<a>` bağlantısının **dışında**
        durur; böylece iç içe etkileşimli öğe sorunu doğmaz ve tıklama
        oynatma/detay bağlantısını tetiklemez.
      */}
      {canDelete && (
        <button
          type="button"
          className="card__delete"
          onClick={() => requestDelete(item)}
          aria-label={`${item.title} dosyasını diskten sil`}
          title="Diskten sil"
        >
          <TrashIcon size={15} />
        </button>
      )}

      {action && <div className="card__action-container">{action}</div>}
    </article>
  );
}
